import React from "react";
import { NavLink } from "react-router-dom";
import Icon from "./Icon";
import { Logo } from "./Logo";
import { cn } from "../../lib/utils";

const ADMIN_SECTIONS = [
  {
    title: "نمای کلی",
    items: [
      {
        label: "داشبورد",
        path: "/admin/dashboard",
        icon: "dashboard",
      },
    ],
  },

  {
    title: "مدیریت ورزشی",
    items: [
      {
        label: "باشگاه‌ها",
        path: "/admin/clubs",
        icon: "club",
      },
      {
        label: "بازیکنان",
        path: "/admin/players",
        icon: "users",
      },
      {
        label: "نقل و انتقالات",
        path: "/admin/transfers",
        icon: "arrowLeft",
      },
      {
        label: "درخواست‌های انتقال",
        path: "/admin/transfer-requests",
        icon: "arrowLeft",
      },
      {
        label: "مسابقات",
        path: "/admin/matches",
        icon: "ball",
      },
      {
        label: "تورنمنت‌ ها",
        path: "/admin/tournaments",
        icon: "trophy",
      },
    ],
  },

  {
    title: "محتوا و ارتباطات",
    items: [
      {
        label: "محتوا",
        path: "/admin/content",
        icon: "news",
      },
      {
        label: "تبلیغات",
        path: "/admin/advertisements",
        icon: "ad",
      },
      {
        label: "تیکت‌ها",
        path: "/admin/tickets",
        icon: "ticket",
      },
    ],
  },

  {
    title: "مدیریت سامانه",
    items: [
      {
        label: "حسابداری",
        path: "/admin/financials",
        icon: "wallet",
      },
      {
        label: "گزارش‌ گیری",
        path: "/admin/reports",
        icon: "report",
      },
      {
        label: "کاربران",
        path: "/admin/users",
        icon: "userGroup",
      },
      {
        label: "هیئت رئیسه و کمیته‌ها",
        path: "/admin/board",
        icon: "award",
      },
      {
        label: "مرکز امنیت",
        path: "/admin/security-center",
        icon: "shield",
      },
      {
        label: "امنیت حساب من",
        path: "/admin/security",
        icon: "shield",
      },
    ],
  },
];

export default function Sidebar({
  collapsed = false,
  onToggle,
  mobile = false,
}) {
  return (
    <aside
      dir="rtl"
      className={cn(
        "relative flex h-full shrink-0 flex-col overflow-hidden bg-ink text-white",
        "rounded-[2rem] shadow-[0_30px_70px_-28px_rgba(7,21,15,0.75)] ring-1 ring-white/5",
        "transition-[width] duration-300 ease-in-out",
        mobile
          ? "w-72"
          : collapsed
            ? "w-[82px]"
            : "w-[270px]"
      )}
    >
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-24 -top-24 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="pitch-stripes absolute inset-0" />
        <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-floodlight/10 blur-3xl" />
      </div>

      {/* Logo */}
      <div
        className={cn(
          "relative z-10 flex h-[72px] shrink-0 items-center border-b border-white/10",
          collapsed && !mobile
            ? "justify-center px-2"
            : "justify-between px-4"
        )}
      >
        {!collapsed || mobile ? (
          <>
            <Logo size="sm" />

            <button
              type="button"
              onClick={onToggle}
              className="hidden h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/60 transition-all hover:bg-white/10 hover:text-white md:grid"
              title="جمع کردن منو"
            >
              <Icon
                name="chevronLeft"
                className="h-4 w-4"
              />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className="grid h-10 w-10 place-items-center rounded-full bg-floodlight text-ink shadow-lime"
            title="باز کردن منو"
          >
            <Icon name="trophy" className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Admin badge */}
      {(!collapsed || mobile) && (
        <div className="relative z-10 px-4 pt-4">
          <div className="flex items-center gap-3 rounded-3xl border border-white/10 bg-gradient-to-l from-floodlight/15 to-transparent p-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-floodlight text-ink">
              <Icon name="shield" className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <p className="truncate text-xs font-extrabold text-white">
                پنل مدیریت
              </p>

              <p className="mt-1 truncate text-[10px] text-white/40">
                دسترسی کامل سامانه
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="relative z-10 flex-1 overflow-y-auto px-3 py-4">
        {ADMIN_SECTIONS.map((section) => (
          <div key={section.title} className="mb-5">
            {(!collapsed || mobile) && (
              <div className="mb-2 px-2">
                <span className="text-[10px] font-extrabold tracking-wider text-white/30">
                  {section.title}
                </span>
              </div>
            )}

            <div className="space-y-1">
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  title={collapsed && !mobile ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      "group relative flex items-center rounded-full transition-all duration-300",
                      collapsed && !mobile
                        ? "justify-center px-2 py-3"
                        : "gap-3 px-3 py-3",
                      isActive
                        ? "bg-floodlight text-ink shadow-lime"
                        : "text-white/55 hover:bg-white/[0.06] hover:text-white"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          "grid h-9 w-9 shrink-0 place-items-center rounded-full transition-all",
                          isActive
                            ? "bg-ink/10"
                            : "bg-white/[0.04] group-hover:bg-white/[0.08]"
                        )}
                      >
                        <Icon
                          name={item.icon}
                          className="h-[18px] w-[18px]"
                        />
                      </span>

                      {(!collapsed || mobile) && (
                        <>
                          <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                            {item.label}
                          </span>

                          {isActive && (
                            <Icon
                              name="chevronLeft"
                              className="h-4 w-4 opacity-70"
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="relative z-10 shrink-0 border-t border-white/10 p-3">
        {(!collapsed || mobile) ? (
          <div className="rounded-2xl bg-white/[0.05] px-3 py-3 text-center">
            <p className="text-[10px] font-medium text-white/30">
              سامانه مدیریت هیئت فوتبال شهرستان پاوه
            </p>

            <p className="mt-1 text-[9px] text-white/20">
              نسخه مدیریتی
            </p>
          </div>
        ) : (
          <div className="mx-auto h-2 w-2 rounded-full bg-floodlight shadow-lg shadow-floodlight/60" />
        )}
      </div>
    </aside>
  );
}